import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaBan, FaBullhorn, FaCode, FaDatabase, FaDiscord, FaDownload, FaExternalLinkAlt, FaFilePdf, FaHeartbeat, FaLock, FaPlus, FaRobot, FaSearch, FaServer, FaSignOutAlt, FaSyncAlt, FaToggleOff, FaToggleOn, FaTrash, FaUndo, FaUserSlash } from "react-icons/fa";
import AppHeader from "../../../components/AppHeader";
import { supabase } from "../../../supabaseClient";
import { ALL_FEATURE_FLAGS } from "../../../config/featureRegistry";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

const defaultBlock = () => ({ id: crypto.randomUUID(), type: "text", content: "", title: "", url: "", description: "", divider: true, spacing: 1 });
const defaultButton = () => ({ id: crypto.randomUUID(), label: "Open link", style: "primary", url: "https://ruht.cloud", emoji: "", disabled: false });

function statusClass(ok) { return ok ? "is-ok" : "is-bad"; }

function blockTypeLabel(type) {
  return ({ text: "Text display", heading: "Heading", subtext: "Subtext", quote: "Quote / callout", separator: "Separator", media: "Media / image" }[type] || "Component");
}

function renderPreviewBlock(block) {
  if (block.type === "heading") return <p key={block.id} className="pguh-discord-preview__heading">{block.content || "Heading"}</p>;
  if (block.type === "subtext") return <p key={block.id} className="pguh-discord-preview__subtext">{block.content || "Secondary supporting text"}</p>;
  if (block.type === "quote") return <blockquote key={block.id} className="pguh-discord-preview__quote">{block.content || "Quoted or callout content"}</blockquote>;
  if (block.type === "separator") return <hr key={block.id} className={`pguh-discord-preview__separator is-spacing-${Number(block.spacing) === 2 ? 2 : 1}`} />;
  if (block.type === "media" && block.url) return <figure key={block.id} className="pguh-discord-preview__media"><img src={block.url} alt={block.description || "Preview media"}/>{block.description && <figcaption>{block.description}</figcaption>}</figure>;
  return <p key={block.id}>{block.content || "Text display"}</p>;
}

const DISCORD_FORMAT_ACTIONS = [
  { key: "bold", label: "Bold", hint: "**bold**" },
  { key: "italic", label: "Italic", hint: "*italic*" },
  { key: "underline", label: "Underline", hint: "__underline__" },
  { key: "strike", label: "Strike", hint: "~~strike~~" },
  { key: "h1", label: "H1", hint: "# Header" },
  { key: "h2", label: "H2", hint: "## Header" },
  { key: "h3", label: "H3", hint: "### Header" },
  { key: "subtext", label: "-#", hint: "Discord subtext" },
  { key: "timestamp", label: "Timestamp", hint: "Full date/time" },
  { key: "relative", label: "Relative", hint: "e.g. in 2 hours" },
  { key: "shorttime", label: "Time", hint: "Short time" },
];

function DiscordFormatToolbar({ onFormat, includeHeadings = true }) {
  return <div className="pguh-discord-format-toolbar" role="toolbar" aria-label="Discord formatting shortcuts">
    {DISCORD_FORMAT_ACTIONS.filter((item) => includeHeadings || !["h1", "h2", "h3", "subtext"].includes(item.key)).map((item) => (
      <button key={item.key} type="button" title={item.hint} onMouseDown={(event) => { event.preventDefault(); onFormat(item.key); }}>{item.label}</button>
    ))}
  </div>;
}

function formatDiscordText(textarea, value, type) {
  const source = String(value || "");
  const start = textarea?.selectionStart ?? source.length;
  const end = textarea?.selectionEnd ?? start;
  const selected = source.slice(start, end);
  const placeholder = selected || "text";
  const unix = Math.floor(Date.now() / 1000);

  let replacement = placeholder;
  if (type === "bold") replacement = `**${placeholder}**`;
  else if (type === "italic") replacement = `*${placeholder}*`;
  else if (type === "underline") replacement = `__${placeholder}__`;
  else if (type === "strike") replacement = `~~${placeholder}~~`;
  else if (type === "h1") replacement = placeholder.split(/\r?\n/).map((line) => `# ${line}`).join("\n");
  else if (type === "h2") replacement = placeholder.split(/\r?\n/).map((line) => `## ${line}`).join("\n");
  else if (type === "h3") replacement = placeholder.split(/\r?\n/).map((line) => `### ${line}`).join("\n");
  else if (type === "subtext") replacement = placeholder.split(/\r?\n/).map((line) => `-# ${line}`).join("\n");
  else if (type === "timestamp") replacement = `<t:${unix}:F>`;
  else if (type === "relative") replacement = `<t:${unix}:R>`;
  else if (type === "shorttime") replacement = `<t:${unix}:t>`;

  const nextValue = `${source.slice(0, start)}${replacement}${source.slice(end)}`;
  return { value: nextValue, start, end: start + replacement.length };
}


export default function DeveloperControls({ session, profile }) {
  const { flags, reload } = useFeatureFlags();
  const [activeTab, setActiveTab] = useState("page");
  const [search, setSearch] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [serviceStatus, setServiceStatus] = useState({ status: "operational", message: "" });
  const [schedule, setSchedule] = useState({ key: "", action: "disable", run_at: "" });
  const [schedules, setSchedules] = useState([]);
  const [discordDestination, setDiscordDestination] = useState("channel");
  const [discordMode, setDiscordMode] = useState("plain");
  const [discord, setDiscord] = useState({ channelId: "", userId: "", plainMessage: "", title: "", subtitle: "", accentColor: "240184", allowEveryone: false, blocks: [defaultBlock()], buttons: [] });
  const [discordBusy, setDiscordBusy] = useState(false);
  const [discordBotState, setDiscordBotState] = useState(null);
  const [discordAdminBusy, setDiscordAdminBusy] = useState("");
  const [discordAdmin, setDiscordAdmin] = useState({ guildId: "", userId: "", reason: "", joinGuildId: "" });
  const [staffAudience, setStaffAudience] = useState(null);
  const [staffAudienceBusy, setStaffAudienceBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const plainMessageRef = useRef(null);
  const blockTextareaRefs = useRef({});

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byTab = ALL_FEATURE_FLAGS.filter((item) => item.category === activeTab);
    if (!q) return byTab;
    return byTab.filter((item) => [item.label, item.description, item.route, item.key].filter(Boolean).some((value) => String(value).toLowerCase().includes(q)));
  }, [activeTab, search]);

  const pageCount = ALL_FEATURE_FLAGS.filter((item) => item.category === "page").length;
  const featureCount = ALL_FEATURE_FLAGS.filter((item) => item.category === "feature").length;

  const authHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    return { "Content-Type": "application/json", Authorization: `Bearer ${data?.session?.access_token || ""}` };
  };

  const developerCall = async (body) => {
    const response = await fetch("/api/developer-console", { method: "POST", headers: await authHeaders(), body: JSON.stringify(body) });
    const raw = await response.text();
    let data = {};
    if (raw) {
      try { data = JSON.parse(raw); }
      catch { data = { error: raw.slice(0, 800) }; }
    }
    if (!response.ok) {
      const detail = data?.details || data?.discord?.message || data?.error || `Request failed (${response.status})`;
      throw new Error(`${detail}${data?.code ? ` [${data.code}]` : ""}`);
    }
    return data;
  };

  const loadDiscordBotState = async () => {
    try {
      const data = await developerCall({ action: "getDiscordBotAdminState" });
      setDiscordBotState(data);
      setDiscordAdmin((current) => ({
        ...current,
        guildId: current.guildId || data.defaultGuildId || "",
        joinGuildId: current.joinGuildId || data.defaultGuildId || "",
      }));
    } catch (error) {
      setDiscordBotState(null);
      setFeedback(error.message);
    }
  };

  const loadStaffAudience = async () => {
    setStaffAudienceBusy(true);
    try {
      const data = await developerCall({ action: "getStaffDiscordAudience" });
      setStaffAudience(data);
    } catch (error) {
      setStaffAudience(null);
      setFeedback(error.message);
    } finally {
      setStaffAudienceBusy(false);
    }
  };

  const downloadSupabaseExport = async () => {
    const confirmed = window.confirm(
      "Download a developer-only archive containing all Supabase PostgREST table data, Auth user/account records and Storage metadata? This file can contain highly sensitive information and should be stored securely."
    );
    if (!confirmed) return;

    setExportBusy(true);
    setFeedback("");
    try {
      const response = await fetch("/api/developer-console", {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ action: "exportSupabaseData" }),
      });

      if (!response.ok) {
        const raw = await response.text();
        let detail = raw || `Export failed (${response.status})`;
        try {
          const parsed = JSON.parse(raw);
          detail = parsed?.details || parsed?.error || detail;
        } catch {}
        throw new Error(detail);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `RUHT_Supabase_Full_Export_${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setFeedback(`Supabase export downloaded (${(blob.size / 1024 / 1024).toFixed(2)} MB).`);
    } catch (error) {
      setFeedback(error.message || "Unable to generate the Supabase export.");
    } finally {
      setExportBusy(false);
    }
  };

  const setEnabled = async (item, enabled) => {
    setBusyKey(item.key); setFeedback("");
    const { error } = await supabase.from("site_feature_flags").upsert({
      key: item.key,
      label: item.label,
      enabled,
      updated_at: new Date().toISOString(),
      updated_by: session?.user?.id || null,
    }, { onConflict: "key" });
    setBusyKey(null);
    if (error) { setFeedback(error.message || "Unable to update this feature."); return; }
    window.dispatchEvent(new CustomEvent("ruht:feature-flags-changed"));
    await reload();
  };

  const enableEverything = async () => {
    if (!window.confirm("Enable every developer-controlled page and feature?")) return;
    setBusyKey("__all__");
    const now = new Date().toISOString();
    const rows = ALL_FEATURE_FLAGS.map((item) => ({
      key: item.key,
      label: item.label,
      enabled: true,
      updated_at: now,
      updated_by: session?.user?.id || null,
    }));
    const { error } = await supabase.from("site_feature_flags").upsert(rows, { onConflict: "key" });
    setBusyKey(null);
    if (error) setFeedback(error.message || "Unable to reset feature flags.");
    else { setFeedback("All pages and features enabled."); window.dispatchEvent(new CustomEvent("ruht:feature-flags-changed")); await reload(); }
  };

  const loadOpsData = async () => {
    const [{ data: status }, { data: sched }] = await Promise.all([
      supabase.from("developer_site_settings").select("value").eq("key", "service_status").maybeSingle(),
      supabase.from("site_feature_schedules").select("*").eq("processed", false).order("run_at", { ascending: true }),
    ]);
    if (status?.value) setServiceStatus({ status: status.value.status || "operational", message: status.value.message || "" });
    setSchedules(sched || []);
  };

  useEffect(() => { if (activeTab === "diagnostics") void loadOpsData(); }, [activeTab]);
  useEffect(() => { if (activeTab === "discord") void loadDiscordBotState(); }, [activeTab]);
  useEffect(() => { if (activeTab === "discord" && discordDestination === "staff-wide") void loadStaffAudience(); }, [activeTab, discordDestination]);

  const saveServiceStatus = async () => {
    setBusyKey("__status__");
    const { error } = await supabase.from("developer_site_settings").upsert({ key: "service_status", value: serviceStatus, updated_by: session?.user?.id || null, updated_at: new Date().toISOString() }, { onConflict: "key" });
    setBusyKey(null); setFeedback(error ? error.message : "Service status saved.");
  };

  const addSchedule = async () => {
    if (!schedule.key || !schedule.run_at) { setFeedback("Choose a control and date/time first."); return; }
    setBusyKey("__schedule__");
    const { error } = await supabase.from("site_feature_schedules").insert({ feature_key: schedule.key, target_enabled: schedule.action === "enable", run_at: new Date(schedule.run_at).toISOString(), created_by: session?.user?.id || null });
    setBusyKey(null); setFeedback(error ? error.message : "Feature schedule created.");
    if (!error) { setSchedule({ key: "", action: "disable", run_at: "" }); await loadOpsData(); }
  };

  const removeSchedule = async (id) => { await supabase.from("site_feature_schedules").delete().eq("id", id); await loadOpsData(); };

  const refreshDiagnostics = async () => {
    setDiagBusy(true); setFeedback("");
    try {
      const data = await developerCall({ action: "diagnostics" });
      setDiagnostics(data);
      if (data?.discordReport?.ok) setFeedback(`Diagnostics completed and posted to Discord channel ${data.discordReport.channelId}.`);
      else if (data?.discordReport) setFeedback(`Diagnostics completed, but the Discord report could not be sent: ${data.discordReport.error || "Unknown Discord error"}`);
    }
    catch (e) { setFeedback(e.message); }
    finally { setDiagBusy(false); }
  };

  const reloadSchema = async () => {
    setDiagBusy(true);
    try { const data = await developerCall({ action: "reloadSchema" }); setFeedback(data.message || "Schema reload requested."); }
    catch (e) { setFeedback(e.message); }
    finally { setDiagBusy(false); }
  };

  const runDiagnosticAction = async (action) => {
    setDiagBusy(true);
    try { const data = await developerCall({ action }); setFeedback(data.message || "Diagnostic check completed."); await refreshDiagnostics(); }
    catch (e) { setFeedback(e.message); }
    finally { setDiagBusy(false); }
  };

  const updateBlock = (id, patch) => setDiscord((d) => ({ ...d, blocks: d.blocks.map((b) => b.id === id ? { ...b, ...patch } : b) }));
  const updateButton = (id, patch) => setDiscord((d) => ({ ...d, buttons: d.buttons.map((b) => b.id === id ? { ...b, ...patch } : b) }));

  const applyPlainFormatting = (type) => {
    const formatted = formatDiscordText(plainMessageRef.current, discord.plainMessage, type);
    setDiscord((current) => ({ ...current, plainMessage: formatted.value.slice(0, 2000) }));
    requestAnimationFrame(() => {
      plainMessageRef.current?.focus();
      plainMessageRef.current?.setSelectionRange(Math.min(formatted.start, 2000), Math.min(formatted.end, 2000));
    });
  };

  const applyBlockFormatting = (blockId, type) => {
    const block = discord.blocks.find((item) => item.id === blockId);
    if (!block) return;
    const textarea = blockTextareaRefs.current[blockId];
    const formatted = formatDiscordText(textarea, block.content, type);
    updateBlock(blockId, { content: formatted.value.slice(0, 4000) });
    requestAnimationFrame(() => {
      const target = blockTextareaRefs.current[blockId];
      target?.focus();
      target?.setSelectionRange(Math.min(formatted.start, 4000), Math.min(formatted.end, 4000));
    });
  };

  const runDiscordMemberAction = async (type) => {
    const guildId = discordAdmin.guildId.trim();
    const userId = discordAdmin.userId.trim();
    const reason = discordAdmin.reason.trim();
    if (!guildId || !userId || !reason) { setFeedback("Server ID, Discord user ID and a reason are required."); return; }

    const verb = type === "ban" ? "ban" : "kick";
    const confirmed = window.confirm(`${verb === "ban" ? "Ban" : "Kick"} Discord user ${userId} from server ${guildId}?\n\nReason: ${reason}`);
    if (!confirmed) return;

    setDiscordAdminBusy(verb);
    setFeedback("");
    try {
      const data = await developerCall({
        action: verb === "ban" ? "discordBanMember" : "discordKickMember",
        guildId,
        userId,
        reason,
      });
      setFeedback(`${verb === "ban" ? "Banned" : "Kicked"} ${data.displayName || userId}. Discord audit-log reason: ${data.reason || reason}`);
      setDiscordAdmin((current) => ({ ...current, userId: "", reason: "" }));
    } catch (error) {
      setFeedback(error.message);
    } finally {
      setDiscordAdminBusy("");
    }
  };

  const leaveDiscordServer = async () => {
    const guildId = discordAdmin.guildId.trim();
    if (!guildId) { setFeedback("Enter the Discord server ID first."); return; }
    const configuredName = discordBotState?.defaultGuild?.id === guildId ? discordBotState.defaultGuild.name : null;
    const confirmed = window.confirm(`Make the RUHT bot leave ${configuredName ? `${configuredName} (${guildId})` : `server ${guildId}`}?\n\nThe bot will immediately lose access to that server until somebody with Manage Server authorises it again.`);
    if (!confirmed) return;

    setDiscordAdminBusy("leave");
    setFeedback("");
    try {
      const data = await developerCall({ action: "discordLeaveGuild", guildId });
      setFeedback(`Bot left ${data.guildName || data.guildId}. Use Join server to authorise it again.`);
      await loadDiscordBotState();
    } catch (error) {
      setFeedback(error.message);
    } finally {
      setDiscordAdminBusy("");
    }
  };

  const openDiscordJoin = () => {
    const clientId = String(discordBotState?.clientId || "").trim();
    if (!clientId) { setFeedback("The Discord bot application ID could not be resolved."); return; }
    const joinGuildId = discordAdmin.joinGuildId.trim();
    if (joinGuildId && !/^\d{15,22}$/.test(joinGuildId)) { setFeedback("Enter a valid Discord server ID or leave the Join server ID blank."); return; }

    const params = new URLSearchParams({
      client_id: clientId,
      permissions: String(discordBotState?.invitePermissions || "84998"),
      scope: "bot applications.commands",
    });
    if (joinGuildId) {
      params.set("guild_id", joinGuildId);
      params.set("disable_guild_select", "true");
    }
    window.open(`https://discord.com/oauth2/authorize?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const sendDiscord = async () => {
    if (discordDestination === "staff-wide") {
      const count = Number(staffAudience?.eligible || 0);
      if (!count) { setFeedback("There are no Staff or Management accounts with a valid Discord ID to message."); return; }
      const confirmed = window.confirm(`Send this ${discordMode === "plain" ? "plain message" : "Components V2 announcement"} as a private DM to ${count} Staff/Management Discord account${count === 1 ? "" : "s"}?`);
      if (!confirmed) return;
    }

    setDiscordBusy(true); setFeedback("");
    try {
      const action = discordDestination === "dm"
        ? "sendDiscordDM"
        : discordDestination === "staff-wide"
          ? "sendStaffWideAnnouncement"
          : "sendDiscord";
      const data = await developerCall({
        action,
        channelId: discord.channelId,
        userId: discord.userId,
        format: discordMode,
        message: discord.plainMessage,
        allowEveryone: discordDestination === "channel" && discord.allowEveryone,
        builder: { title: discord.title, subtitle: discord.subtitle, accentColor: Number(discord.accentColor || 0), allowEveryone: discordDestination === "channel" && discord.allowEveryone, blocks: discord.blocks, buttons: discord.buttons },
      });
      if (discordDestination === "staff-wide") {
        const skipped = Number(data?.audience?.missingDiscordId || 0) + Number(data?.audience?.invalidDiscordId || 0) + Number(data?.audience?.duplicateDiscordId || 0);
        setFeedback(`Staff-wide announcement complete: ${data.sentCount || 0} sent, ${data.failedCount || 0} failed${skipped ? `, ${skipped} skipped because no usable unique Discord ID was available` : ""}.`);
        await loadStaffAudience();
      } else {
        setFeedback(`${discordMode === "plain" ? "Plain" : "Components V2"} Discord ${discordDestination === "dm" ? "DM" : "message"} sent. Message ID: ${data.messageId}`);
      }
    } catch (e) { setFeedback(e.message); }
    finally { setDiscordBusy(false); }
  };

  const renderGroup = (category, title, description) => (
    <section className="pguh-dev-section">
      <div className="pguh-dev-section__heading"><div><span className="pguh-eyebrow">{category === "page" ? "Navigation & routes" : "Functional controls"}</span><h2>{title}</h2><p>{description}</p></div><strong>{visible.length}</strong></div>
      <div className="pguh-dev-flags">
        {visible.map((item) => {
          const enabled = flags[item.key] !== false;
          return <article className={`pguh-dev-flag${enabled ? " is-enabled" : " is-disabled"}`} key={item.key}>
            <div className="pguh-dev-flag__content"><div className="pguh-dev-flag__title"><strong>{item.label}</strong><span>{enabled ? "Enabled" : "Disabled"}</span></div><p>{item.description}</p>{item.route && <code>{item.route}</code>}<small>{item.key}</small></div>
            <button type="button" className={`pguh-dev-toggle ${enabled ? "is-on" : "is-off"}`} disabled={busyKey === item.key || busyKey === "__all__"} onClick={() => setEnabled(item, !enabled)} aria-pressed={enabled}>{enabled ? <FaToggleOn /> : <FaToggleOff />}<span>{busyKey === item.key ? "Saving…" : enabled ? "Enabled" : "Disabled"}</span></button>
          </article>;
        })}
        {!visible.length && <p className="pguh-dev-empty">No controls match your search.</p>}
      </div>
    </section>
  );

  const renderDiagnostics = () => <>
    <section className="pguh-dev-section"><div className="pguh-dev-section__heading"><div><span className="pguh-eyebrow">Service status</span><h2>Global service status</h2><p>Publish an operational state and optional message for administrators to reference.</p></div></div>
      <div className="pguh-dev-form-grid"><label>Status<select className="nhsuk-select" value={serviceStatus.status} onChange={(e)=>setServiceStatus((s)=>({...s,status:e.target.value}))}><option value="operational">Operational</option><option value="degraded">Degraded</option><option value="major_disruption">Major disruption</option><option value="maintenance">Maintenance</option></select></label><label className="is-wide">Message<textarea className="nhsuk-textarea" rows="3" value={serviceStatus.message} onChange={(e)=>setServiceStatus((s)=>({...s,message:e.target.value}))} /></label><button className="nhsuk-button" type="button" onClick={saveServiceStatus} disabled={busyKey==="__status__"}>Save service status</button></div>
    </section>

    <section className="pguh-dev-section"><div className="pguh-dev-section__heading"><div><span className="pguh-eyebrow">Scheduled controls</span><h2>Feature scheduling</h2><p>Queue a feature or page to automatically enable or disable at a chosen time.</p></div><strong>{schedules.length}</strong></div>
      <div className="pguh-dev-schedule"><select className="nhsuk-select" value={schedule.key} onChange={(e)=>setSchedule((s)=>({...s,key:e.target.value}))}><option value="">Choose control…</option>{ALL_FEATURE_FLAGS.map((f)=><option key={f.key} value={f.key}>{f.label}</option>)}</select><select className="nhsuk-select" value={schedule.action} onChange={(e)=>setSchedule((s)=>({...s,action:e.target.value}))}><option value="disable">Disable</option><option value="enable">Enable</option></select><input className="nhsuk-input" type="datetime-local" value={schedule.run_at} onChange={(e)=>setSchedule((s)=>({...s,run_at:e.target.value}))}/><button className="nhsuk-button" type="button" onClick={addSchedule}>Schedule</button></div>
      <div className="pguh-dev-schedule-list">{schedules.map((row)=><div key={row.id}><span><strong>{ALL_FEATURE_FLAGS.find((f)=>f.key===row.feature_key)?.label || row.feature_key}</strong><small>{row.target_enabled ? "Enable" : "Disable"} · {new Date(row.run_at).toLocaleString("en-GB")}</small></span><button type="button" onClick={()=>removeSchedule(row.id)} aria-label="Remove schedule"><FaTrash /></button></div>)}{!schedules.length && <p>No pending schedules.</p>}</div>
    </section>

    <section className="pguh-dev-section"><div className="pguh-dev-section__heading"><div><span className="pguh-eyebrow">Diagnostics</span><h2>System diagnostics</h2><p>Check Supabase, Discord, maintenance and recent scheduled-job health.</p></div></div>
      <div className="pguh-dev-diagnostic-actions"><button className="nhsuk-button" type="button" onClick={refreshDiagnostics} disabled={diagBusy}><FaSyncAlt /> {diagBusy ? "Checking…" : "Run diagnostics"}</button><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={()=>runDiagnosticAction("checkShiftNotifications")} disabled={diagBusy}>Run shift notification check</button><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={()=>runDiagnosticAction("checkPatientArchive")} disabled={diagBusy}>Run patient archive check</button><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={reloadSchema} disabled={diagBusy}>Force schema cache reload</button><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={reload}>Refresh feature flags</button></div>
      {diagnostics && <div className="pguh-dev-diagnostics-grid"><div className={statusClass(diagnostics.supabase?.ok)}><strong>Supabase</strong><span>{diagnostics.supabase?.ok ? "Connected" : "Table scan issue"}</span><small>{diagnostics.supabase?.populatedTableCount ?? 0} populated tables · {Number(diagnostics.supabase?.totalRows || 0).toLocaleString("en-GB")} rows</small></div><div className={statusClass(diagnostics.discord?.bot?.ok)}><strong>Discord bot</strong><span>{diagnostics.discord?.bot?.ok ? diagnostics.discord.bot.username : "Unavailable"}</span><small>{diagnostics.discord?.bot?.error || (diagnostics.discord?.bot?.configured ? "Token configured" : "Token missing")}</small></div><div className={statusClass(diagnostics.discord?.guild?.ok)}><strong>Discord guild</strong><span>{diagnostics.discord?.guild?.name || "Unavailable"}</span><small>{diagnostics.discord?.guild?.id || "Guild ID not connected"}</small></div><div className="is-ok"><strong>Maintenance</strong><span>{diagnostics.maintenance?.enabled ? "Enabled" : "Disabled"}</span><small>{diagnostics.maintenance?.updated_at ? new Date(diagnostics.maintenance.updated_at).toLocaleString("en-GB") : "No timestamp"}</small></div><div className="is-ok"><strong>Disabled controls</strong><span>{diagnostics.disabledFlags?.length || 0}</span><small>pages/features currently disabled</small></div><div className="is-ok"><strong>Environment</strong><span>{diagnostics.environment?.cronSecret ? "Cron configured" : "Cron secret missing"}</span><small>{diagnostics.environment?.publicSiteUrl}</small></div></div>}
      {diagnostics?.supabase?.tableCountError && <div className="nhsuk-warning-callout"><h3 className="nhsuk-warning-callout__label">Supabase table scan warning</h3><p>{diagnostics.supabase.tableCountError}</p></div>}
      {diagnostics?.supabase?.tables?.length > 0 && <div className="pguh-dev-supabase-tables"><div className="pguh-dev-supabase-tables__heading"><div><span className="pguh-eyebrow">Supabase data</span><h3>Populated public tables</h3><p>Exact live row counts. Empty tables are hidden automatically.</p></div><strong>{diagnostics.supabase.tables.length}</strong></div><div className="pguh-dev-supabase-tables__list">{diagnostics.supabase.tables.map((table)=><div key={table.table_name}><code>{table.table_name}</code><strong>{Number(table.row_count || 0).toLocaleString("en-GB")}</strong><span>rows</span></div>)}</div><div className="pguh-dev-supabase-tables__total"><span>Total rows across populated tables</span><strong>{Number(diagnostics.supabase.totalRows || 0).toLocaleString("en-GB")}</strong></div></div>}
      {diagnostics?.jobs?.length > 0 && <div className="pguh-dev-jobs"><h3>Recent job/API activity</h3>{diagnostics.jobs.map((job,i)=><div key={`${job.job_key}-${i}`}><strong>{job.job_key}</strong><span>{job.status}</span><small>{new Date(job.created_at).toLocaleString("en-GB")}</small></div>)}</div>}
    </section>
  </>;

  const renderDataExport = () => <section className="pguh-dev-section pguh-dev-export">
    <div className="pguh-dev-section__heading">
      <div>
        <span className="pguh-eyebrow">Developer data access</span>
        <h2><FaDatabase /> Full Supabase export</h2>
        <p>Generate one ZIP containing a PDF for every Supabase relation exposed through the project API, plus Auth account/login records and Storage metadata.</p>
      </div>
    </div>

    <div className="nhsuk-warning-callout pguh-dev-export__warning">
      <h3 className="nhsuk-warning-callout__label"><FaLock /> Sensitive developer export</h3>
      <p>The archive can contain emails, Discord and Roblox identifiers, staff records, patient/eCAMIS records, applications, audit records and any other data stored in exposed Supabase tables. Keep downloaded exports private and delete them when they are no longer required.</p>
    </div>

    <div className="pguh-dev-export__grid">
      <article>
        <FaFilePdf />
        <div><strong>Every exposed table/relation</strong><span>One complete PDF per relation, including empty relations.</span></div>
      </article>
      <article>
        <FaLock />
        <div><strong>Auth users and login fields</strong><span>Includes account details returned by Supabase Auth Admin, such as email and latest sign-in timestamps.</span></div>
      </article>
      <article>
        <FaDatabase />
        <div><strong>Storage metadata</strong><span>Includes bucket and object/file metadata. Raw stored file binaries and Supabase platform secrets are not placed in the database archive.</span></div>
      </article>
    </div>

    <div className="pguh-dev-export__actions">
      <button className="nhsuk-button" type="button" onClick={downloadSupabaseExport} disabled={exportBusy}>
        <FaDownload /> {exportBusy ? "Building secure export…" : "Download full Supabase data ZIP"}
      </button>
      <p>Each download also includes an <code>EXPORT_MANIFEST.pdf</code> and <code>EXPORT_MANIFEST.json</code> describing exactly what was included.</p>
    </div>
  </section>;

  const renderDiscord = () => <section className="pguh-dev-section pguh-discord-console">
    <div className="pguh-dev-section__heading pguh-discord-console__heading">
      <div>
        <span className="pguh-eyebrow">Discord bot</span>
        <h2><FaDiscord /> Message console</h2>
        <p>Send normal or Components V2 messages to a Discord channel, one user, or every Staff and Management account with a linked Discord ID.</p>
      </div>
    </div>

    <section className="pguh-discord-admin-panel">
      <div className="pguh-discord-card-heading">
        <div>
          <span className="pguh-eyebrow">Bot administration</span>
          <h3><FaRobot /> Server and member controls</h3>
          <p>Moderate Discord members, remove the bot from a server, or open Discord's authorisation flow to add it to a server.</p>
        </div>
        <button className="nhsuk-button nhsuk-button--secondary pguh-discord-admin-refresh" type="button" onClick={loadDiscordBotState} disabled={Boolean(discordAdminBusy)}><FaSyncAlt /> Refresh bot</button>
      </div>

      <div className="pguh-discord-bot-status">
        <div><span>Bot</span><strong>{discordBotState?.bot ? `${discordBotState.bot.globalName || discordBotState.bot.username} (${discordBotState.bot.id})` : "Checking…"}</strong></div>
        <div><span>Configured server</span><strong>{discordBotState?.defaultGuild ? `${discordBotState.defaultGuild.name} (${discordBotState.defaultGuild.id})` : discordBotState?.defaultGuildId || "Not configured"}</strong></div>
      </div>

      <div className="pguh-discord-admin-grid">
        <article className="pguh-discord-admin-card">
          <div className="pguh-discord-admin-card__title"><FaUserSlash /><div><strong>Kick or ban user</strong><small>The bot requires Kick Members / Ban Members and its role must be above the target member's highest role.</small></div></div>
          <label><span>Server ID</span><input className="nhsuk-input" value={discordAdmin.guildId} onChange={(e)=>setDiscordAdmin((d)=>({...d,guildId:e.target.value.replace(/\D/g,"")}))} placeholder="123456789012345678" inputMode="numeric" /></label>
          <label><span>Discord user ID</span><input className="nhsuk-input" value={discordAdmin.userId} onChange={(e)=>setDiscordAdmin((d)=>({...d,userId:e.target.value.replace(/\D/g,"")}))} placeholder="123456789012345678" inputMode="numeric" /></label>
          <label><span>Reason</span><textarea className="nhsuk-textarea" rows="3" maxLength="512" value={discordAdmin.reason} onChange={(e)=>setDiscordAdmin((d)=>({...d,reason:e.target.value}))} placeholder="Reason shown in the Discord audit log"/><small>{discordAdmin.reason.length}/512</small></label>
          <div className="pguh-discord-admin-actions">
            <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={()=>runDiscordMemberAction("kick")} disabled={Boolean(discordAdminBusy)}><FaUserSlash /> {discordAdminBusy === "kick" ? "Kicking…" : "Kick user"}</button>
            <button className="nhsuk-button pguh-discord-danger-button" type="button" onClick={()=>runDiscordMemberAction("ban")} disabled={Boolean(discordAdminBusy)}><FaBan /> {discordAdminBusy === "ban" ? "Banning…" : "Ban user"}</button>
          </div>
        </article>

        <article className="pguh-discord-admin-card">
          <div className="pguh-discord-admin-card__title"><FaServer /><div><strong>Server membership</strong><small>Leaving is immediate. Joining requires Discord OAuth authorisation by an account with Manage Server; bots cannot silently add themselves to a server.</small></div></div>
          <label><span>Server ID to leave</span><input className="nhsuk-input" value={discordAdmin.guildId} onChange={(e)=>setDiscordAdmin((d)=>({...d,guildId:e.target.value.replace(/\D/g,"")}))} placeholder="123456789012345678" inputMode="numeric" /></label>
          <button className="nhsuk-button nhsuk-button--secondary pguh-discord-leave-button" type="button" onClick={leaveDiscordServer} disabled={Boolean(discordAdminBusy)}><FaSignOutAlt /> {discordAdminBusy === "leave" ? "Leaving…" : "Leave server"}</button>
          <hr/>
          <label><span>Server ID to join</span><input className="nhsuk-input" value={discordAdmin.joinGuildId} onChange={(e)=>setDiscordAdmin((d)=>({...d,joinGuildId:e.target.value.replace(/\D/g,"")}))} placeholder="Optional - preselect a server" inputMode="numeric" /><small>Leave blank to choose a server on Discord's authorisation screen.</small></label>
          <button className="nhsuk-button" type="button" onClick={openDiscordJoin} disabled={!discordBotState?.clientId}><FaExternalLinkAlt /> Join server via Discord</button>
        </article>
      </div>
    </section>

    <div className="pguh-discord-destination-switch" role="tablist" aria-label="Discord destination type">
      <button type="button" className={discordDestination === "channel" ? "is-active" : ""} onClick={() => setDiscordDestination("channel")} aria-selected={discordDestination === "channel"}>
        <strong>Channel message</strong>
        <span>Send to a Discord channel using its channel ID.</span>
      </button>
      <button type="button" className={discordDestination === "dm" ? "is-active" : ""} onClick={() => setDiscordDestination("dm")} aria-selected={discordDestination === "dm"}>
        <strong>Direct message</strong>
        <span>Send privately to one Discord user using their user ID.</span>
      </button>
      <button type="button" className={discordDestination === "staff-wide" ? "is-active" : ""} onClick={() => setDiscordDestination("staff-wide")} aria-selected={discordDestination === "staff-wide"}>
        <strong><FaBullhorn /> Staff wide announcement</strong>
        <span>DM every Staff and Management profile that has a valid Discord ID.</span>
      </button>
    </div>

    <div className="pguh-discord-mode-switch" role="tablist" aria-label="Discord message type">
      <button type="button" className={discordMode === "plain" ? "is-active" : ""} onClick={() => setDiscordMode("plain")} aria-selected={discordMode === "plain"}>
        <strong>Plain message</strong>
        <span>Send normal Discord text with markdown and mentions.</span>
      </button>
      <button type="button" className={discordMode === "components" ? "is-active" : ""} onClick={() => setDiscordMode("components")} aria-selected={discordMode === "components"}>
        <strong>Components V2 embed</strong>
        <span>Build a richer announcement using headings, text, callouts, media, separators and buttons.</span>
      </button>
    </div>

    <div className={`pguh-discord-channel-card${discordDestination === "staff-wide" ? " pguh-discord-channel-card--staff" : ""}`}>
      {discordDestination === "channel" && <label>
        <span>Channel ID</span>
        <small>Paste the ID of a channel the bot can access.</small>
        <input className="nhsuk-input" value={discord.channelId} onChange={(e)=>setDiscord((d)=>({...d,channelId:e.target.value.replace(/\D/g,"")}))} placeholder="123456789012345678" inputMode="numeric" />
      </label>}
      {discordDestination === "dm" && <label>
        <span>Discord user ID</span>
        <small>Paste the recipient's Discord user ID. The bot will create or reuse a private DM channel automatically.</small>
        <input className="nhsuk-input" value={discord.userId} onChange={(e)=>setDiscord((d)=>({...d,userId:e.target.value.replace(/\D/g,"")}))} placeholder="123456789012345678" inputMode="numeric" />
      </label>}
      {discordDestination === "staff-wide" && <div className="pguh-discord-staff-audience">
        <div>
          <span className="pguh-eyebrow">Automatic audience</span>
          <strong>{staffAudienceBusy ? "Checking staff records…" : `${Number(staffAudience?.eligible || 0).toLocaleString("en-GB")} Discord recipient${Number(staffAudience?.eligible || 0) === 1 ? "" : "s"}`}</strong>
          <small>Collected server-side from every profile whose account type is Staff or Management. Duplicate Discord IDs are only messaged once.</small>
        </div>
        <div className="pguh-discord-staff-audience__stats">
          <span><strong>{staffAudience?.totalProfiles ?? "—"}</strong>Total staff / management</span>
          <span><strong>{staffAudience?.eligible ?? "—"}</strong>Valid unique IDs</span>
          <span><strong>{staffAudience?.missingDiscordId ?? "—"}</strong>Missing IDs</span>
          <span><strong>{staffAudience?.invalidDiscordId ?? "—"}</strong>Invalid IDs</span>
        </div>
        <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={loadStaffAudience} disabled={staffAudienceBusy}>{staffAudienceBusy ? "Refreshing…" : "Refresh audience"}</button>
      </div>}
      {discordDestination === "channel" && <label className="pguh-discord-mention-toggle">
        <input type="checkbox" checked={discord.allowEveryone} onChange={(e)=>setDiscord((d)=>({...d,allowEveryone:e.target.checked}))}/>
        <span><strong>Allow @everyone and role mentions</strong><small>Leave disabled unless the message intentionally needs to ping a wider audience.</small></span>
      </label>}
      {discordDestination !== "channel" && <div className="pguh-discord-dm-note"><strong>Private delivery</strong><small>{discordDestination === "staff-wide" ? "Each person receives their own private DM from the RUHT bot. @everyone and role mentions are disabled for staff-wide delivery." : "This message is sent as a private DM. @everyone and role mentions are disabled."}</small></div>}
    </div>

    {discordMode === "plain" ? <div className="pguh-discord-plain-layout">
      <div className="pguh-discord-editor-card">
        <div className="pguh-discord-card-heading"><div><span className="pguh-eyebrow">Message</span><h3>Plain Discord message</h3></div><span>{discord.plainMessage.length}/2000</span></div>
        <div className="pguh-discord-message-field">
          <label htmlFor="pguh-discord-plain-message">Message content</label>
          <DiscordFormatToolbar onFormat={applyPlainFormatting} />
          <textarea id="pguh-discord-plain-message" ref={plainMessageRef} className="nhsuk-textarea" rows="10" maxLength="2000" value={discord.plainMessage} onChange={(e)=>setDiscord((d)=>({...d,plainMessage:e.target.value}))} placeholder="Type your message here. Discord markdown is supported."/>
          <small>Select text and use the shortcuts above. Timestamp buttons insert Discord timestamp tags using the current time.</small>
        </div>
        <div className="pguh-discord-sendbar"><button className="nhsuk-button" type="button" onClick={sendDiscord} disabled={discordBusy || !discord.plainMessage.trim() || (discordDestination === "dm" && !discord.userId) || (discordDestination === "channel" && !discord.channelId) || (discordDestination === "staff-wide" && !Number(staffAudience?.eligible || 0))}>{discordBusy ? "Sending…" : discordDestination === "dm" ? "Send plain DM" : discordDestination === "staff-wide" ? "Send staff wide announcement" : "Send plain message"}</button></div>
      </div>
      <aside className="pguh-discord-preview pguh-discord-preview--plain">
        <span>LIVE PREVIEW</span>
        <div className="pguh-discord-message-preview">
          <div className="pguh-discord-avatar">PG</div>
          <div><div className="pguh-discord-message-meta"><strong>PGUH Bot</strong><span>APP</span><small>Today</small></div><p>{discord.plainMessage || "Your message will appear here."}</p></div>
        </div>
        <small>Preview approximates Discord. Final markdown rendering is controlled by Discord.</small>
      </aside>
    </div> : <div className="pguh-discord-builder">
      <div className="pguh-discord-builder__editor">
        <section className="pguh-discord-editor-card">
          <div className="pguh-discord-card-heading"><div><span className="pguh-eyebrow">Container</span><h3>Message details</h3><p>Set the overall container content used for the Components V2 message.</p></div></div>
          <div className="pguh-discord-fields-grid">
            <label><span>Title</span><input className="nhsuk-input" value={discord.title} onChange={(e)=>setDiscord((d)=>({...d,title:e.target.value}))} placeholder="Message title"/></label>
            <label><span>Subtitle</span><input className="nhsuk-input" value={discord.subtitle} onChange={(e)=>setDiscord((d)=>({...d,subtitle:e.target.value}))} placeholder="Optional subtitle"/></label>
            <label><span>Accent colour</span><input className="nhsuk-input" value={discord.accentColor} onChange={(e)=>setDiscord((d)=>({...d,accentColor:e.target.value.replace(/\D/g,"")}))} placeholder="240184"/><small>Discord decimal colour value.</small></label>
          </div>
        </section>

        <section className="pguh-discord-editor-card">
          <div className="pguh-discord-card-heading"><div><span className="pguh-eyebrow">Content</span><h3>Components</h3><p>Build your message in order. Available types include heading, text, subtext, callout, separator and media.</p></div><strong>{discord.blocks.length}</strong></div>
          <div className="pguh-discord-items">
            {discord.blocks.map((block,index)=><div className="pguh-discord-item-card" key={block.id}>
              <div className="pguh-discord-item-card__header">
                <div className="pguh-discord-item-card__meta"><span className="pguh-discord-item-card__index">{index + 1}</span><div><strong>{blockTypeLabel(block.type)}</strong><small>Component {index + 1}</small></div></div>
                <button type="button" className="pguh-discord-remove" onClick={()=>setDiscord((d)=>({...d,blocks:d.blocks.filter((b)=>b.id!==block.id)}))} aria-label={`Remove component ${index + 1}`}><FaTrash /></button>
              </div>
              <div className="pguh-discord-item-card__body">
                <label className="pguh-discord-field pguh-discord-field--type"><span>Component type</span><select className="nhsuk-select" value={block.type} onChange={(e)=>updateBlock(block.id,{type:e.target.value})}><option value="text">Text display</option><option value="heading">Heading</option><option value="subtext">Subtext</option><option value="quote">Quote / callout</option><option value="separator">Separator</option><option value="media">Media / image URL</option></select></label>
                {(block.type==="text" || block.type==="heading" || block.type==="subtext" || block.type==="quote") && <div className="pguh-discord-field pguh-discord-field--full"><span>{block.type === "heading" ? "Heading text" : block.type === "subtext" ? "Subtext" : block.type === "quote" ? "Callout text" : "Text content"}</span><DiscordFormatToolbar includeHeadings={block.type === "text"} onFormat={(type)=>applyBlockFormatting(block.id,type)} /><textarea ref={(node)=>{ if (node) blockTextareaRefs.current[block.id]=node; else delete blockTextareaRefs.current[block.id]; }} className="nhsuk-textarea" rows={block.type === "subtext" ? 3 : 5} value={block.content||""} onChange={(e)=>updateBlock(block.id,{content:e.target.value})} placeholder={block.type === "heading" ? "Important update" : block.type === "subtext" ? "Supporting information" : block.type === "quote" ? "Quoted or highlighted information" : "Markdown supported"}/><small>{block.type === "heading" ? "Renders as a prominent heading in the message." : block.type === "subtext" ? "Useful for quiet supporting text beneath main content." : block.type === "quote" ? "Useful for highlighted notes, warnings or quoted text." : "Select text and use the formatting shortcuts above."}</small></div>}
                {block.type==="separator" && <>
                  <label className="pguh-discord-field"><span>Divider line</span><select className="nhsuk-select" value={block.divider === false ? "off" : "on"} onChange={(e)=>updateBlock(block.id,{divider:e.target.value !== "off"})}><option value="on">Show divider</option><option value="off">No divider</option></select></label>
                  <label className="pguh-discord-field"><span>Spacing</span><select className="nhsuk-select" value={String(Number(block.spacing) === 2 ? 2 : 1)} onChange={(e)=>updateBlock(block.id,{spacing:Number(e.target.value)})}><option value="1">Standard</option><option value="2">Large</option></select></label>
                  <div className="pguh-discord-helper">A Discord separator component will be inserted here.</div>
                </>}
                {block.type==="media" && <>
                  <label className="pguh-discord-field pguh-discord-field--wide"><span>Media URL</span><input className="nhsuk-input" value={block.url||""} onChange={(e)=>updateBlock(block.id,{url:e.target.value})} placeholder="https://..."/></label>
                  <label className="pguh-discord-field pguh-discord-field--wide"><span>Image description</span><input className="nhsuk-input" value={block.description||""} onChange={(e)=>updateBlock(block.id,{description:e.target.value})} placeholder="Optional description or caption"/></label>
                </>}
              </div>
            </div>)}
          </div>
          <button className="nhsuk-button nhsuk-button--secondary pguh-discord-add" type="button" onClick={()=>setDiscord((d)=>({...d,blocks:[...d.blocks,defaultBlock()]}))}><FaPlus /> Add component</button>
        </section>

        <section className="pguh-discord-editor-card">
          <div className="pguh-discord-card-heading"><div><span className="pguh-eyebrow">Actions</span><h3>Buttons</h3><p>Add up to 25 buttons. Link buttons open a URL; other button styles use a custom interaction ID.</p></div><strong>{discord.buttons.length}/25</strong></div>
          <div className="pguh-discord-items">
            {discord.buttons.map((button,index)=><div className="pguh-discord-item-card pguh-discord-item-card--button" key={button.id}>
              <div className="pguh-discord-item-card__header">
                <div className="pguh-discord-item-card__meta"><span className="pguh-discord-item-card__index">{index + 1}</span><div><strong>{button.label || `Button ${index + 1}`}</strong><small>{button.style === "link" ? "Link button" : "Interactive button"}</small></div></div>
                <button type="button" className="pguh-discord-remove" onClick={()=>setDiscord((d)=>({...d,buttons:d.buttons.filter((b)=>b.id!==button.id)}))} aria-label={`Remove button ${index + 1}`}><FaTrash /></button>
              </div>
              <div className="pguh-discord-button-fields">
                <label><span>Label</span><input className="nhsuk-input" value={button.label} onChange={(e)=>updateButton(button.id,{label:e.target.value})} placeholder="Button label"/></label>
                <label><span>Style</span><select className="nhsuk-select" value={button.style} onChange={(e)=>updateButton(button.id,{style:e.target.value})}><option value="primary">Primary</option><option value="secondary">Secondary</option><option value="success">Success</option><option value="danger">Danger</option><option value="link">Link</option></select></label>
                <label><span>{button.style === "link" ? "URL" : "Custom ID / URL"}</span><input className="nhsuk-input" value={button.url||""} onChange={(e)=>updateButton(button.id,{url:e.target.value})} placeholder={button.style === "link" ? "https://..." : "Optional URL or interaction value"}/></label>
                <label><span>Emoji</span><input className="nhsuk-input" value={button.emoji||""} onChange={(e)=>updateButton(button.id,{emoji:e.target.value})} placeholder="Optional"/></label>
              </div>
            </div>)}
          </div>
          <button className="nhsuk-button nhsuk-button--secondary pguh-discord-add" type="button" disabled={discord.buttons.length>=25} onClick={()=>setDiscord((d)=>({...d,buttons:[...d.buttons,defaultButton()]}))}><FaPlus /> Add button</button>
        </section>

        <div className="pguh-discord-sendbar"><button className="nhsuk-button" type="button" onClick={sendDiscord} disabled={discordBusy || (discordDestination === "dm" && !discord.userId) || (discordDestination === "channel" && !discord.channelId) || (discordDestination === "staff-wide" && !Number(staffAudience?.eligible || 0))}>{discordBusy ? "Sending…" : discordDestination === "dm" ? "Send Components V2 DM" : discordDestination === "staff-wide" ? "Send staff wide announcement" : "Send Components V2 message"}</button></div>
      </div>

      <aside className="pguh-discord-preview">
        <span>LIVE COMPONENTS V2 PREVIEW</span>
        <div className="pguh-discord-shell">
          <div className="pguh-discord-message-preview">
            <div className="pguh-discord-avatar">PG</div>
            <div className="pguh-discord-message-body">
              <div className="pguh-discord-message-meta"><strong>PGUH Bot</strong><span>APP</span><small>Today</small></div>
              <div className="pguh-discord-preview__card" style={{borderLeftColor:`#${Math.max(0,Math.min(0xffffff,Number(discord.accentColor||0))).toString(16).padStart(6,"0")}`}}>
                {discord.title&&<h3>{discord.title}</h3>}
                {discord.subtitle&&<small>{discord.subtitle}</small>}
                {discord.blocks.map((b)=>renderPreviewBlock(b))}
              </div>
              {discord.buttons.length>0&&<div className="pguh-discord-preview__buttons">{discord.buttons.map((b)=><button type="button" className={`is-${b.style}`} key={b.id} disabled>{b.emoji?`${b.emoji} `:""}{b.label}</button>)}</div>}
            </div>
          </div>
        </div>
        <small>Preview approximates Discord Components V2 while retaining the NHS-styled editing layout.</small>
      </aside>
    </div>}
  </section>;

  return <><AppHeader variant="management" session={session} profile={profile}/><main className="nhsuk-main-wrapper pguh-developer-controls" role="main"><div className="nhsuk-width-container"><header className="pguh-dev-hero"><div><span className="pguh-eyebrow">Developer only</span><h1><FaCode /> Site operations console</h1><p>Control pages and features, schedule operational changes, diagnose integrations, export Supabase data, administer the Discord bot and publish plain or Components V2 Discord messages.</p></div><button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={enableEverything} disabled={busyKey==="__all__"}><FaUndo /> {busyKey==="__all__"?"Resetting…":"Enable everything"}</button></header><div className="pguh-dev-warning"><strong>Developer access:</strong> only <code>cjaystreamz@gmail.com</code> can open this area or change these values.</div>{feedback&&<div className="nhsuk-inset-text">{feedback}</div>}
    <div className="pguh-dev-tabs" role="tablist" aria-label="Developer controls"><button type="button" className={activeTab==="page"?"is-active":""} onClick={()=>{setActiveTab("page");setSearch("")}}><span>Pages</span><strong>{pageCount}</strong></button><button type="button" className={activeTab==="feature"?"is-active":""} onClick={()=>{setActiveTab("feature");setSearch("")}}><span>Features</span><strong>{featureCount}</strong></button><button type="button" className={activeTab==="diagnostics"?"is-active":""} onClick={()=>{setActiveTab("diagnostics");setSearch("")}}><span>Diagnostics</span><FaHeartbeat /></button><button type="button" className={activeTab==="data-export"?"is-active":""} onClick={()=>{setActiveTab("data-export");setSearch("")}}><span>Data export</span><FaDatabase /></button><button type="button" className={activeTab==="discord"?"is-active":""} onClick={()=>{setActiveTab("discord");setSearch("")}}><span>Discord</span><FaDiscord /></button></div>
    {(activeTab==="page"||activeTab==="feature")&&<label className="pguh-dev-search"><span><FaSearch /> Search {activeTab==="page"?"pages":"features"}</span><input className="nhsuk-input" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder={activeTab==="page"?"Search page, route or key":"Search feature or key"}/></label>}
    {activeTab==="page"&&renderGroup("page","Pages","Disabled pages are removed from supported navigation and direct URL access is blocked.")}{activeTab==="feature"&&renderGroup("feature","Features","Disable specific operations without taking the containing page offline.")}{activeTab==="diagnostics"&&renderDiagnostics()}{activeTab==="data-export"&&renderDataExport()}{activeTab==="discord"&&renderDiscord()}
  </div></main></>;
}
